"use client";

import React, { useEffect, useState, useRef } from "react";
import { cn, tinaField } from "@/utils";
import { marketingPageSchemas } from "@/lib/seo/page-schemas";
import { breadcrumbSchema } from "@/lib/seo/structured-data";
import {
  hasBreadcrumbOverrides,
  resolveBreadcrumbs,
  type BreadcrumbOverride,
} from "@/lib/seo/breadcrumbs";
import { BreadcrumbOverrideProvider } from "@/components/Breadcrumbs";

type StructuredDataItem = Record<string, unknown>;
export type StructuredData =
  | StructuredDataItem
  | Array<StructuredDataItem | null | undefined>;

/**
 * Serialize a JSON-LD payload safely for inclusion in a `<script>` tag.
 * Escapes characters that could break out of the tag or that bots
 * sometimes choke on when evaluating the payload as a JS string.
 */
function safeStringify(payload: StructuredDataItem): string {
  return JSON.stringify(payload)
    .replace(/</g, "\\u003c")
    .replace(/\u2028/g, "\\u2028")
    .replace(/\u2029/g, "\\u2029");
}

/**
 * Render one or more `<script type="application/ld+json">` tags from a
 * schema object or array. Returns `null` when the payload is empty so
 * callers can pass conditionally-built schemas without `&&` guards.
 */
function renderJsonLd(schema: StructuredData | null | undefined) {
  if (!schema) return null;
  const items: StructuredDataItem[] = Array.isArray(schema)
    ? schema.filter((item): item is StructuredDataItem => !!item)
    : [schema];
  if (items.length === 0) return null;
  return items.map((item, index) => (
    <script
      key={index}
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: safeStringify(item) }}
    />
  ));
}

function applyBreadcrumbOverride(
  data: StructuredData | null,
  pagePath: string | undefined,
  overrides: BreadcrumbOverride[] | null | undefined
): StructuredData | null {
  if (!data || !pagePath || !hasBreadcrumbOverrides(overrides)) return data;
  const schema = breadcrumbSchema(
    resolveBreadcrumbs({ pagePath, overrides })
  );
  if (!schema) return data;

  const items = (Array.isArray(data) ? data : [data]).filter(
    (item): item is StructuredDataItem => !!item
  );
  const index = items.findIndex((item) => item["@type"] === "BreadcrumbList");
  if (index >= 0) {
    items[index] = schema;
    return items;
  }
  return [...items, schema];
}

export const useIsEditing = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const isInIframe = window.self !== window.top;
      setIsEditing(isInIframe);
    }
  }, []);

  useEffect(() => {
    if (!isEditing) {
      setIsOpen(false);
      return;
    }

    const checkOpen = () => {
      const hasClass = document.body.classList.contains(
        "__tina-quick-editing-enabled"
      );
      setIsOpen(hasClass);
    };

    // Check immediately
    checkOpen();

    // Watch for class changes
    const observer = new MutationObserver(checkOpen);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, [isEditing]);

  return { isEditing, isOpen };
};

interface PageSeoLike {
  title?: string | null;
  description?: string | null;
  image?: string | null;
  breadcrumbs?: BreadcrumbOverride[] | null;
}

export default function Page({
  className,
  children,
  defaultTinaField,
  seo,
  pagePath,
  titleFallback,
  faqs,
  structuredData,
}: {
  className?: string;
  children: React.ReactNode;
  defaultTinaField?: any;
  /**
   * Tina SEO block from the page document. When `pagePath` is also
   * supplied, Page auto-emits WebPage + BreadcrumbList JSON-LD (and a
   * FAQPage entry when `faqs` is populated). Resource detail pages
   * that need Article / Video / Report schemas should bypass this and
   * pass a pre-built `structuredData` prop instead.
   */
  seo?: PageSeoLike | null;
  pagePath?: string;
  /** Used for the WebPage `name` and final breadcrumb crumb when seo.title is blank. */
  titleFallback?: string;
  /** Tina `faqs` block; ignored unless `seo` + `pagePath` are also provided. */
  faqs?: unknown;
  /**
   * Escape hatch for pages whose schema isn't a marketing WebPage
   * (e.g. articles, listings, reports). Takes priority over `seo` and
   * is rendered as-is.
   */
  structuredData?: StructuredData;
}) {
  const { isEditing } = useIsEditing();
  const tinaFieldRef = useRef<HTMLDivElement>(null);
  const hasClickedRef = useRef(false);

  useEffect(() => {
    if (!isEditing || hasClickedRef.current) return;

    const handleTinaEnabled = () => {
      if (hasClickedRef.current) return;

      if (document.body.classList.contains("__tina-quick-editing-enabled")) {
        setTimeout(() => {
          tinaFieldRef.current?.click();
          hasClickedRef.current = true;
        }, 300);
      }
    };

    // Check immediately in case already enabled
    handleTinaEnabled();

    // Watch for class being added
    const observer = new MutationObserver(handleTinaEnabled);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, [isEditing]);

  const resolvedStructuredData: StructuredData | null = applyBreadcrumbOverride(
    structuredData ??
      (seo && pagePath
        ? (marketingPageSchemas({
            pagePath,
            title:
              (typeof seo.title === "string" && seo.title.trim()) ||
              titleFallback ||
              "Binarly",
            description: seo.description,
            image: seo.image,
            faqs,
            breadcrumbs: seo.breadcrumbs,
          }) as StructuredData)
        : null),
    pagePath,
    seo?.breadcrumbs
  );

  return (
    <BreadcrumbOverrideProvider overrides={seo?.breadcrumbs}>
      <div className={cn(className, "flex-1")}>
        {renderJsonLd(resolvedStructuredData)}
        {defaultTinaField && (
          <div
            ref={tinaFieldRef}
            data-tina-field={tinaField(defaultTinaField)}
            className="hidden"
          ></div>
        )}
        {children}
      </div>
    </BreadcrumbOverrideProvider>
  );
}
